import {
  AbstractModelFactory,
  DeserializeEvent,
} from "@projectstorm/react-canvas-core";
import {
  LinkModel,
  PortModel,
  PortModelAlignment,
  PortModelGenerics,
  PortModelOptions,
} from "@projectstorm/react-diagrams-core";
import { DefaultLinkModel } from "@projectstorm/react-diagrams-defaults";

/**
 * Interface for the class port arguments.
 *
 * @interface
 * @augments {PortModelOptions}
 */
export interface ClassPortModelOptions extends PortModelOptions
{
  /**
   * The label.
   *
   * @property {string} label The label.
   */
  label?: string;

  /**
   * A value indicating whether the port has an input possibility.
   *
   * @property {boolean} in A value indicating whether the port has an input possibility.
   */
  in?: boolean;

  /**
   * The type.
   *
   * @property {string} type The type.
   */
  type?: string;

  /**
   * The ID.
   *
   * @property {string} id The ID.
   */
  id?: string;
}

/**
 * Interface for the class port generics.
 *
 * @interface
 * @augments {PortModelGenerics}
 */
export interface ClassPortModelGenerics extends PortModelGenerics
{
  /**
   * The options.
   *
   * @property {ClassPortModelOptions} OPTIONS The options.
   */
  OPTIONS: ClassPortModelOptions;
}

/**
 * The class port label.
 *
 * @class
 * @augments {PortModel<ClassPortModelGenerics>}
 */
export class ClassPortModel extends PortModel<ClassPortModelGenerics>
{
  /**
   * The options.
   *
   * @property {ClassPortModelOptions} options The options.
   */
  options: ClassPortModelOptions;

  /**
   * Create an instance of CustomPortModel.
   *
   * @param {boolean} isIn A value indicating whether the port should be an in port.
   * @param {string} id The ID.
   * @param {string} name The name.
   * @param {string} label The label.
   */
  constructor(isIn: boolean, id?: string, name?: string, label?: string);

  /**
   * Create an instance of CustomPortModel.
   *
   * @param {ClassPortModelOptions} options The options.
   */
  constructor(options: ClassPortModelOptions);

  /**
   * Create an instance of ClassPortModel.
   *
   * @param {ClassPortModelOptions | boolean} options The options.
   * @param {string} id The ID.
   * @param {string} name The name.
   * @param {string} label The label.
   */
  constructor(
    options: ClassPortModelOptions | boolean,
    id?: string,
    name?: string,
    label?: string,
  )
  {
    // check if name has an value
    if (name)
    {
      options = {
        in: !!options,
        id: id,
        name: name,
        label: label,
      };
    }

    options = options as ClassPortModelOptions;

    super({
      label: options.label || options.name,
      alignment: options.in
        ? PortModelAlignment.TOP
        : PortModelAlignment.BOTTOM,
      type: "class-port",
      ...options,
    });

    this.options = options;
  }

  /**
   * Deserialize the model.
   *
   * @param {DeserializeEvent<this>} event The event arguments.
   * @returns {void}
   */
  deserialize(event: DeserializeEvent<this>)
  {
    super.deserialize(event);
    this.options.in = event.data.in;
    this.options.label = event.data.label;
  }

  /**
   * Serialize the model.
   *
   * @returns {any} The serialized entity.
   */
  serialize()
  {
    return {
      ...super.serialize(),
      in: this.options.in,
      label: this.options.label,
    };
  }

  /**
   * Link to this port.
   *
   * @template {LinkModel} T The link model type.
   * @param {PortModel} port The port.
   * @param {AbstractModelFactory<T>} factory The factory.
   * @returns {T} A link model instance.
   */
  link<T extends LinkModel>(
    port: PortModel,
    factory?: AbstractModelFactory<T>,
  ): T
  {
    const link = this.createLinkModel(factory);

    link.setSourcePort(this);
    link.setTargetPort(port);

    return link as T;
  }

  /**
   * Check if the passed port can link to this port.
   *
   * @param {PortModel} port The port.
   * @returns {boolean} A value indicating whether a link is supported.
   */
  canLinkToPort(port: PortModel): boolean
  {
    if (port instanceof ClassPortModel)
    {
      return this.options.in !== port.getOptions().in;
    }

    return true;
  }

  /**
   * Create the link model.
   *
   * @param {AbstractModelFactory<LinkModel>} factory The factory.
   * @returns {LinkModel} A link model.
   */
  createLinkModel(factory?: AbstractModelFactory<LinkModel>): LinkModel
  {
    const link = super.createLinkModel();

    if (!link && factory)
    {
      return factory.generateModel({});
    }

    return link || new DefaultLinkModel();
  }
}